import React from 'react';
import { FileText, Mail, Building2, Calendar, AlertTriangle } from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import './ModernTermsPage.scss';

const ModernTermsPage = () => {
    const termsData = [
        {
            title: "DEFINITIONS",
            content: [
                '"Buyer": The entity purchasing Goods from Seller.',
                '"Goods": Products, materials, or services specified in the Order.',
                '"Order": Buyer\'s purchase order, quote acceptance, or written order confirmation.',
                '"Seller": Jaak Tech (DBA Jaak Tech), FEIN 86-1260376, Cage Code 9FK10, DUNS 124995677, Unique ID D244FV1CHVP4.',
                '"FOB": Free On Board (Incoterms 2020).',
            ],
        },
        {
            title: "ACCEPTANCE & ORDER OF PRECEDENCE",
            content: [
                "Binding Agreement: Buyer's Order constitutes unconditional acceptance of these Terms. Any conflicting terms in Buyer's documentation are expressly rejected.",
                "Precedence: These Terms supersede all prior agreements. Signed written amendments by both parties prevail.",
            ],
        },
        {
            title: "PRICES & PAYMENT",
            content: [
                "Currency: All prices in U.S. dollars (USD).",
                "Exclusions: Prices exclude: Federal, state, or local taxes (e.g., sales/use tax). Duties, tariffs, shipping, handling, insurance.",
                "Payment Terms: Net 30 days from invoice date unless otherwise agreed. Late Payments: Late payment fines + reasonable collection costs. Withholding: Buyer may not withhold payment for unrelated claims.",
                "Credits: Past-due accounts may suspend future shipments.",
            ],
        },
        {
            title: "SHIPPING & DELIVERY",
            content: [
                "Shipping Terms: U.S. Government: FOB Destination (Seller bears risk until delivery). Commercial Buyers: FOB Origin (Buyer bears risk upon carrier handoff).",
                "Title Transfer: Title passes to Buyer upon delivery to the carrier (FOB Origin) or destination (FOB Destination).",
                "Delivery Schedule: Estimated delivery dates are non-binding. Seller notifies Buyer of material delays. Partial Shipments: Allowed unless Buyer specifies otherwise.",
                "Inspection: Buyer must inspect Goods within 7 days of receipt. Concealed defects reported within 14 days.",
            ],
        },
        {
            title: "CHANGES & CANCELLATIONS",
            content: [
                "Changes: Buyer requests in writing. The seller may adjust pricing/delivery and charge for incurred costs.",
                "Cancellations: Accepted Orders may be canceled only with the Seller's written consent. Buyer liable for: (a) Finished Goods costs; (b) Work-in-process costs; (c) Cancellation fees.",
            ],
        },
        {
            title: "LIMITED WARRANTY",
            content: [
                "Coverage: Goods conform to Seller's published specifications and are free from material/workmanship defects for 90 days after delivery.",
                "Exclusions: Warranty voids if: Goods are misused, modified, or improperly stored. Defects arise from the Buyer's design, specifications, or third-party components.",
                "Sole Remedy: Seller, at its option, will: (a) Repair/replace defective Goods; or (b) Refund purchase price.",
                "Disclaimer: ALL IMPLIED WARRANTIES (INCLUDING MERCHANTABILITY AND FITNESS FOR PURPOSE) ARE EXPRESSLY DISCLAIMED. SELLER'S LIABILITY IS LIMITED TO THE REMEDIES ABOVE.",
            ],
        },
        {
            title: "LIMITATION OF LIABILITY",
            content: [
                "Cap on Damages: Seller's total liability shall not exceed the purchase price of the affected Goods.",
                "Excluded Damages: No liability for indirect, consequential, punitive, or lost-profit damages.",
                "Exceptions: Caps/exclusions do not apply to: (a) Breaches of confidentiality; (b) Indemnification obligations; (c) Gross negligence/willful misconduct.",
            ],
        },
        {
            title: "INTELLECTUAL PROPERTY (IP) & DATA",
            content: [
                "Ownership: Seller retains all IP rights in Goods, designs, and documentation.",
                "Indemnification: Seller defends Buyer against third-party IP infringement claims related to Goods, subject to Buyer's prompt notice and cooperation.",
                "Data Security: Seller complies with applicable data privacy laws (e.g., CCPA, GDPR if applicable).",
            ],
        },
        {
            title: "CONFIDENTIALITY",
            content: [
                "Obligation: Both parties protect each other's confidential information using reasonable care.",
                "Exclusions: Information that is: (a) publicly known; (b) independently developed; (c) rightfully received from third parties.",
            ],
        },
        {
            title: "COMPLIANCE & GOVERNMENT TERMS",
            content: [
                "Export Controls: Buyer complies with U.S. export laws (EAR, ITAR) and sanctions programs.",
                "Government Sales: FAR/DFARS Clauses: Flow-down required clauses for U.S. government sales (e.g., 52.204-21, 52.219-8). Audit Rights: U.S. government retains audit rights per FAR 52.215-2.",
            ],
        },
        {
            title: "TERMINATION",
            content: [
                "For Cause: Either party may terminate for material breach if uncured within 30 days of notice.",
                "Insolvency: Immediate termination if either party becomes insolvent or files for bankruptcy.",
            ],
        },
        {
            title: "FORCE MAJEURE",
            content: [
                "Seller not liable for delays/failures due to: war, terrorism, natural disasters, pandemics, embargoes, or supply chain disruptions beyond its control.",
            ],
        },
        {
            title: "DISPUTE RESOLUTION & GOVERNING LAW",
            content: [
                "Governing Law: Maryland law (excluding conflicts principles).",
                "Disputes: Parties negotiate in good faith for 30 days. Unresolved disputes are litigated in federal or state courts in Prince George's County, Maryland.",
            ],
        },
        {
            title: "MISCELLANEOUS",
            content: [
                "Assignment: Buyer may not assign rights without Seller's consent.",
                "Notices: Sent via certified mail/email to addresses in the Order.",
                "Waiver: Failure to enforce a right is not a waiver.",
                "Severability: Invalid provisions are replaced with valid ones of similar intent.",
                "Survival: Sections on Payment, Warranty, Limitation of Liability, IP, and Confidentiality survive termination.",
            ],
        },
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Terms & Conditions"
                subtitle="Jaak Tech website terms & conditions - your access and usage rights"
                breadcrumbs={[{ label: 'Terms & Conditions' }]}
            />

            {/* Last Updated */}
            <section className="jk-terms-meta">
                <div className="jk-terms-meta__container">
                    <div className="jk-terms-meta__badge">
                        <FileText size={18} />
                        <span>Legal Document</span>
                    </div>
                    <div className="jk-terms-meta__date">
                        <Calendar size={18} />
                        <span>Updated on: May 10th, 2026</span>
                    </div>
                </div>
            </section>

            {/* Terms Content */}
            <section className="jk-terms-content">
                <div className="jk-terms-content__container">
                    {termsData.map((section, index) => (
                        <div key={index} className="jk-terms-section">
                            <h2 className="jk-terms-section__title">
                                <span className="jk-terms-section__number">{index + 1}.</span>
                                {section.title}
                            </h2>
                            <div className="jk-terms-section__content">
                                {section.content.map((item, itemIndex) => (
                                    <p key={itemIndex} className="jk-terms-section__item">
                                        {item.includes(":") ? (
                                            <>
                                                <strong className="jk-terms-section__label">
                                                    {item.split(":")[0]}:
                                                </strong>
                                                {item.split(":").slice(1).join(":")}
                                            </>
                                        ) : (
                                            item
                                        )}
                                    </p>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Contact Section */}
                    <div className="jk-terms-contact">
                        <div className="jk-terms-contact__icon">
                            <Mail size={24} />
                        </div>
                        <h2 className="jk-terms-contact__title">Contact Information</h2>
                        <p className="jk-terms-contact__text">
                            For questions regarding these Terms & Conditions or other related issues, please contact us:
                        </p>
                        <div className="jk-terms-contact__details">
                            <div className="jk-terms-contact__item">
                                <strong>Email:</strong>
                                <a href="mailto:info@jaaktech.net">info@jaaktech.net</a>
                            </div>
                            <div className="jk-terms-contact__address">
                                <Building2 size={20} />
                                <div>
                                    <strong>Jaak Tech</strong>
                                    <span>3342 Bladensburg Rd, Suite B06</span>
                                    <span>Brentwood, Maryland 20722</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Acknowledgment */}
                    <div className="jk-terms-acknowledgment">
                        <AlertTriangle size={24} />
                        <h3 className="jk-terms-acknowledgment__title">ACKNOWLEDGMENT</h3>
                        <p className="jk-terms-acknowledgment__text">
                            BY PLACING AN ORDER, THE BUYER ACKNOWLEDGES HAVING READ, UNDERSTOOD, AND AGREED TO THESE TERMS.
                        </p>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
};

export default ModernTermsPage;